// Veyl — OpenCV.js optical flow tracker for høykvalitets object tracking.
// Lastes on-demand fra CDN (~9 MB) første gang tracking aktiveres.
// Cachet etter første lasting.

const OPENCV_CDN_URL = "https://docs.opencv.org/4.8.0/opencv.js";

let cvLoadPromise = null;
let cvInstance = null;

/**
 * Last inn OpenCV.js dynamisk. Cache instansen etter første lasting.
 * @returns {Promise<Object>} cv-modulen
 */
export async function loadOpenCV() {
  if (cvInstance) return cvInstance;
  if (cvLoadPromise) return cvLoadPromise;

  cvLoadPromise = new Promise((resolve, reject) => {
    // Sjekk om allerede globalt tilgjengelig (fra tidligere lasting)
    if (typeof window !== "undefined" && window.cv && window.cv.calcOpticalFlowPyrLK) {
      cvInstance = window.cv;
      return resolve(window.cv);
    }

    const existing = document.querySelector(`script[src="${OPENCV_CDN_URL}"]`);
    if (existing) {
      // Script tag allerede lagt til, vent på ready
      waitForCv(resolve, reject);
      return;
    }

    const script = document.createElement("script");
    script.src = OPENCV_CDN_URL;
    script.async = true;
    script.onload = () => waitForCv(resolve, reject);
    script.onerror = () => reject(new Error("Kunne ikke laste OpenCV.js"));
    document.head.appendChild(script);
  });

  return cvLoadPromise;
}

function waitForCv(resolve, reject, tries = 0) {
  if (typeof window !== "undefined" && window.cv && window.cv.calcOpticalFlowPyrLK) {
    cvInstance = window.cv;
    return resolve(window.cv);
  }
  // OpenCV bruker onRuntimeInitialized. Prøv å hooke den, eller poll.
  if (typeof window !== "undefined" && window.cv && !window.cv.calcOpticalFlowPyrLK) {
    window.cv.onRuntimeInitialized = () => {
      cvInstance = window.cv;
      resolve(window.cv);
    };
    // Backup — poll i 30 sek
    if (tries < 300) {
      setTimeout(() => waitForCv(resolve, reject, tries + 1), 100);
    } else {
      reject(new Error("OpenCV timeout"));
    }
    return;
  }
  if (tries < 300) {
    setTimeout(() => waitForCv(resolve, reject, tries + 1), 100);
  } else {
    reject(new Error("OpenCV timeout"));
  }
}

/**
 * Sparse Optical Flow Tracker basert på Lucas-Kanade.
 *
 * Slik brukes den:
 *   const tracker = new OpticalFlowTracker(cv);
 *   tracker.init(canvas, polygonPoints);         // frame 0
 *   const delta = tracker.update(canvas);        // hver ny frame
 *   // delta = { dx, dy, scale } eller null hvis mistet
 *   tracker.dispose();                            // rydd opp minne
 */
export class OpticalFlowTracker {
  constructor(cv) {
    this.cv = cv;
    this.prevGray = null;
    this.prevPts = null;
    this.initialCenter = null;
    this.initialSpread = null; // gjennomsnittlig avstand fra senter → skala-estimering
    this.lost = false;
  }

  /**
   * Initialiser tracker med et frame + polygon.
   * @param {HTMLCanvasElement} canvas
   * @param {Array<{x,y}>} polygon
   * @returns {boolean} true hvis nok features funnet
   */
  init(canvas, polygon) {
    const cv = this.cv;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of polygon) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }
    const bbox = {
      x: Math.max(0, Math.floor(minX)),
      y: Math.max(0, Math.floor(minY)),
      w: Math.min(canvas.width, Math.ceil(maxX - minX)),
      h: Math.min(canvas.height, Math.ceil(maxY - minY)),
    };
    if (bbox.w < 10 || bbox.h < 10) return false;

    const src = cv.imread(canvas);
    const gray = new cv.Mat();
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
    src.delete();

    // Bruk mask så vi kun finner features INNENFOR polygonets bbox
    const mask = new cv.Mat.zeros(gray.rows, gray.cols, cv.CV_8UC1);
    const rect = new cv.Rect(bbox.x, bbox.y, bbox.w, bbox.h);
    const maskRoi = mask.roi(rect);
    maskRoi.setTo(new cv.Scalar(255));
    maskRoi.delete();

    const corners = new cv.Mat();
    // goodFeaturesToTrack: (image, corners, maxCorners, qualityLevel, minDistance, mask, blockSize)
    cv.goodFeaturesToTrack(gray, corners, 80, 0.01, 4, mask, 3, false, 0.04);
    mask.delete();

    if (corners.rows < 5) {
      corners.delete();
      gray.delete();
      return false;
    }

    this.prevGray = gray;
    this.prevPts = corners;
    this.initialCenter = { x: (bbox.x + bbox.w / 2), y: (bbox.y + bbox.h / 2) };
    this.initialSpread = this._computeSpread(corners, this.initialCenter);
    this.lost = false;
    return true;
  }

  /**
   * Oppdater tracker med nytt frame.
   * @param {HTMLCanvasElement} canvas
   * @returns {{dx:number, dy:number, scale:number} | null}
   */
  update(canvas) {
    if (this.lost || !this.prevGray || !this.prevPts) return null;
    const cv = this.cv;

    const src = cv.imread(canvas);
    const gray = new cv.Mat();
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
    src.delete();

    const nextPts = new cv.Mat();
    const status = new cv.Mat();
    const err = new cv.Mat();

    try {
      cv.calcOpticalFlowPyrLK(
        this.prevGray, gray, this.prevPts, nextPts, status, err
      );
    } catch (e) {
      nextPts.delete(); status.delete(); err.delete(); gray.delete();
      this.lost = true;
      return null;
    }

    // Samle valide punkter
    const validNew = [];
    const validOld = [];
    for (let i = 0; i < status.rows; i++) {
      if (status.data[i] === 1) {
        validNew.push({
          x: nextPts.data32F[i * 2],
          y: nextPts.data32F[i * 2 + 1],
        });
        validOld.push({
          x: this.prevPts.data32F[i * 2],
          y: this.prevPts.data32F[i * 2 + 1],
        });
      }
    }

    status.delete();
    err.delete();

    // Trenger minst 4 valide punkter for stabil måling
    if (validNew.length < 4) {
      nextPts.delete();
      this.prevGray.delete();
      this.prevGray = gray;
      // Behold gamle pts som fallback
      this.lost = true;
      return null;
    }

    // Median-basert translasjon (robust mot outliers)
    const dxs = validNew.map((p, i) => p.x - validOld[i].x).sort((a, b) => a - b);
    const dys = validNew.map((p, i) => p.y - validOld[i].y).sort((a, b) => a - b);
    const dx = dxs[Math.floor(dxs.length / 2)];
    const dy = dys[Math.floor(dys.length / 2)];

    // Skala fra endring i punkt-spredning
    const newCenter = {
      x: this.initialCenter.x + this._cumulativeDx() + dx,
      y: this.initialCenter.y + this._cumulativeDy() + dy,
    };
    const newSpread = this._computeSpreadFromPoints(validNew, newCenter);
    let scale = 1.0;
    if (this.initialSpread > 5 && newSpread > 5) {
      scale = newSpread / this.initialSpread;
      // Klipp skala-endring per frame (unngå hopp)
      scale = Math.max(0.85, Math.min(1.15, scale));
    }

    // Oppdater state
    this.prevGray.delete();
    this.prevGray = gray;
    this.prevPts.delete();
    this.prevPts = nextPts;

    // Akkumuler for total delta
    this._addDelta(dx, dy);

    return { dx: this._cumulativeDx(), dy: this._cumulativeDy(), scale };
  }

  _addDelta(dx, dy) {
    this._cumDx = (this._cumDx || 0) + dx;
    this._cumDy = (this._cumDy || 0) + dy;
  }
  _cumulativeDx() { return this._cumDx || 0; }
  _cumulativeDy() { return this._cumDy || 0; }

  _computeSpread(cornersMat, center) {
    let sum = 0;
    for (let i = 0; i < cornersMat.rows; i++) {
      const x = cornersMat.data32F[i * 2];
      const y = cornersMat.data32F[i * 2 + 1];
      sum += Math.hypot(x - center.x, y - center.y);
    }
    return sum / cornersMat.rows;
  }

  _computeSpreadFromPoints(points, center) {
    let sum = 0;
    for (const p of points) {
      sum += Math.hypot(p.x - center.x, p.y - center.y);
    }
    return sum / points.length;
  }

  /**
   * Nullstill tracker til initial-tilstand. Kalles ved video-rewind.
   */
  reset() {
    this._cumDx = 0;
    this._cumDy = 0;
    this.lost = false;
  }

  dispose() {
    try {
      if (this.prevGray) { this.prevGray.delete(); this.prevGray = null; }
      if (this.prevPts) { this.prevPts.delete(); this.prevPts = null; }
    } catch { /* ignorer */ }
  }
}
